import { RowSelectionState } from '@tanstack/react-table';
import { useCallback, useEffect, useRef, useState } from 'react';

export type UseRowsSelectionReturnType = ReturnType<typeof useRowsSelection>;

export const useRowsSelection = <T extends { tag?: string }>(
  rows: T[],
  defaultRowSelection?: RowSelectionState
) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(
    defaultRowSelection ?? {}
  );
  const rowsRef = useRef<T[]>(rows);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  const updateRowSelection = useCallback(
    (rowId: string, selected: boolean) =>
      setRowSelection(prev => {
        const next = { ...prev };

        if (prev[rowId]) {
          delete next[rowId];
        } else {
          next[rowId] = selected;
        }

        return next;
      }),
    []
  );

  const toggleAllRowsSelection = useCallback((selected: boolean) => {
    if (selected) {
      const updatedRowSelection = rowsRef.current.reduce(
        (acc: Record<string, boolean>, data) => {
          if (data.tag) {
            acc[data.tag] = selected;
          }

          return acc;
        },
        {}
      );
      setRowSelection(updatedRowSelection);
    } else {
      setRowSelection({});
    }
  }, []);

  return {
    rowSelection,
    setRowSelection,
    updateRowSelection,
    toggleAllRowsSelection,
  };
};
